import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Contest} from '../../../model/contest/Contest';
import {BaseModalComponent} from '../../../common/modal/base-modal/base-modal.component';
import {Role} from '../../../model/auth/Role';
import {ProgressService} from '../../../service/progress.service';
import {ContestTaskService} from '../../../service/contest.task.service';
import {ContestProgress} from '../../../model/contest/ContestEvent';
import {NotificationHandler} from '../../../helper/notification.handler';
import {
  ConfirmationContext,
  ConfirmationData,
  ConfirmationModalComponent
} from '../../../common/modal/confirmation-modal/confirmation-modal.component';

export interface ProgressDetailsData {
  userId: string;
  firstName: string;
  lastName: string;
  competitionId: string;
  contest: Contest;
}

@Component({
  selector: 'exam-progress-modal',
  templateUrl: './exam-progress-modal.component.html',
  styleUrls: ['./exam-progress-modal.component.scss']
})
export class ExamProgressModalComponent extends BaseModalComponent implements OnInit {

  readonly roles = [Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  readonly contest: Contest;
  readonly competitionId: string;

  progress?: ContestProgress;

  private readonly userId: string;
  readonly userName: string;

  constructor(private contestTaskService: ContestTaskService,
              private dialog: MatDialog,
              dialogRef: MatDialogRef<ExamProgressModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: ProgressDetailsData) {
    super(dialogRef, progressService);

    this.userId = data.userId;
    this.userName = `${data.firstName} ${data.lastName}`;

    this.competitionId = data.competitionId;
    this.contest = data.contest;
  }

  ngOnInit(): void {
    this.loadProgress();
  }

  private loadProgress() {
    this.startProgress();
    this.contestTaskService.getContestProgress(this.contest.id, this.userId).subscribe(progress => {
      this.progress = progress;

      this.stopProgress();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Contest progress details fetch has failed message@@notifications-contest-progress-details-fetch-failed-error:Contest progress details fetch has failed`, error);

      this.stopProgress();
    });
  }

  get notParticipated(): boolean {
    return !this.progress || !this.progress.startedAt;
  }

  get participating(): boolean {
    return !!this.progress
      && !!this.progress.startedAt
      && (!this.progress.finishedAt
        && (this.progress.startedAt.getTime() + (this.progress.contestDuration || 0)) > new Date().getTime());
  }

  get participated(): boolean {
    return !!this.progress
      && !!this.progress.startedAt
      && (!!this.progress.finishedAt
        || (this.progress.startedAt.getTime() + (this.progress.contestDuration || 0)) <= new Date().getTime());
  }

  get answeredQuestionsCount(): number {
    return this.progress && this.progress.answers
      ? Object.keys(this.progress.answers).length
      : 0;
  }

  proceedAllowed(): boolean {
    const now = new Date();

    return !!this.progress
      && !!this.progress.startedAt
      && !!this.contest
      && !!this.contest.startsAt
      && this.contest.startsAt < now
      && !!this.contest.endsAt
      && this.contest.endsAt > now;
  }

  proceed(): void {
    const data: ConfirmationData = {
      title: $localize`:General|Clear progress action label@@general-clear-progress-action-label:Clear Progress`,
      content: $localize`:Exam Progress|Clear progress details@@exam-progress-clear-progress-details:If you proceed, all progress and answers will be deleted, and the participant will be allowed to retake the contest. Do you want to continue?`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      context.modal.proceedHandled();

      this.startProgress();

      this.contestTaskService.clearContestProgress(this.competitionId, this.contest.id, this.userId).subscribe(() => {
        this.closeModal();
      }, error => {
        this.stopProgress();
        NotificationHandler.handleError($localize`:Notifications|Clear contest progress has failed message@@notifications-clear-contest-progress-failed-error:Clear contest progress has failed`, error);
      });
    });
  }

}
